import axios from 'axios';
import { BASE_URL } from '../api/config';
import type { Voyageurs } from '../models/Voyageurs';

// Bouton « Lire le passeport » de la fiche voyageur (API /lecture-passeport du backend admin, lecture seule).
// La lecture PROPOSE des valeurs ; la fiche n'est enregistrée que par son bouton « Valider » (PUT /voyageurs/:id).
// Les fonctions pures (application à la fiche, textes) sont testées par lecturePasseportServices.test.ts.

export type StatutLu = 'sur' | 'verifier';
export type ChampLu =
  | 'nompax' | 'prenompax' | 'numeropax' | 'datenaissance' | 'sexe' | 'ville_naissance'
  | 'datedelivrance' | 'dateexpiration' | 'adresse' | 'cp' | 'ville';

export interface ValeurLue {
  valeur: string;
  statut: StatutLu;    // sur : contrôlé ; verifier : lu une seule fois, sans contrôle possible
  note: string;        // d'où vient la valeur
  remplacer: boolean;  // false : proposé seulement si le champ est vide (domicile imprimé, peut être ancien)
}

export interface LecturePasseportReussie {
  ok: true;
  document: { type: 'passeport' | 'carte_identite' | 'autre'; code: string; format: string; pays: string; nationalite: string };
  mrz_valide: boolean;
  champs: Partial<Record<ChampLu, ValeurLue>>;
  avertissements: string[];
  page: number | null;
  duree_ms: number;
}

export interface LecturePasseportEchec {
  ok: false;
  raison: 'pas_de_passeport' | 'format_non_gere' | 'mrz_introuvable' | 'mrz_illisible';
  message: string;
}

export type LecturePasseport = LecturePasseportReussie | LecturePasseportEchec;

// Une lecture prend de 2 à 30 s (photo tournée ou penchée) ; au-delà de 3 min, on abandonne
export const DELAI_LECTURE_MS = 180000;

export const lirePasseport = async (idVoyageur: number): Promise<LecturePasseport> => {
  const r = await axios.get(`${BASE_URL}/lecture-passeport/voyageurs/${idVoyageur}`, { timeout: DELAI_LECTURE_MS });
  return r.data;
};

// Message d'erreur réseau / HTTP pour l'opérateur
export const messageErreurLecture = (e: unknown): string => {
  if (axios.isAxiosError(e)) {
    const corps = e.response && e.response.data as { message?: string } | undefined;
    if (corps && corps.message) return corps.message;
    if (e.code === 'ECONNABORTED') return 'Lecture trop longue : réessayer, ou saisir les champs à la main.';
    if (e.response && e.response.status === 404) return 'Lecture du passeport indisponible sur ce serveur (backend admin à mettre à jour).';
  }
  return 'Lecture du passeport impossible : réessayer, ou saisir les champs à la main.';
};

// ---------------------------------------------------------------------------
// Application à la fiche (PUR)
// ---------------------------------------------------------------------------
// Marque d'un champ de la fiche après lecture
export interface MarqueChamp {
  etat: 'sur' | 'verifier' | 'non_repris';
  note: string;
  ancien?: string;   // valeur de la fiche remplacée (différente)
  propose?: string;  // valeur lue, non reprise (champ déjà rempli)
}
export type MarquesLecture = Partial<Record<ChampLu, MarqueChamp>>;

export interface LectureAppliquee {
  voyageur: Voyageurs;
  marques: MarquesLecture;
  nbRemplis: number;      // champs changés
  nbConfirmes: number;    // champs déjà justes
  nbAVerifier: number;    // parmi les remplis et confirmés, ceux à vérifier
  nbNonRepris: number;    // valeurs lues laissées de côté (champ déjà rempli)
}

// Ordre d'affichage (celui de la fiche)
export const CHAMPS_LUS: ChampLu[] = [
  'nompax', 'prenompax', 'numeropax', 'datenaissance', 'sexe', 'ville_naissance', 'datedelivrance', 'dateexpiration',
  'adresse', 'cp', 'ville',
];

// Comparaison insensible à la casse, aux accents et aux espaces (« Dupont » = « DUPONT »)
const comparable = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();

export const appliquerLecture = (voyageur: Voyageurs, lecture: LecturePasseportReussie): LectureAppliquee => {
  const suivant: Voyageurs = { ...voyageur };
  const marques: MarquesLecture = {};
  let nbRemplis = 0;
  let nbConfirmes = 0;
  let nbAVerifier = 0;
  let nbNonRepris = 0;
  CHAMPS_LUS.forEach((cle) => {
    const lu = lecture.champs[cle];
    if (!lu || !lu.valeur) return;
    const actuel = String(voyageur[cle] || '').trim();
    const identique = comparable(actuel) === comparable(lu.valeur);
    if (actuel && !identique && !lu.remplacer) {
      marques[cle] = { etat: 'non_repris', note: lu.note, propose: lu.valeur };
      nbNonRepris++;
      return;
    }
    if (identique) {
      nbConfirmes++;
    } else {
      suivant[cle] = lu.valeur;
      nbRemplis++;
    }
    if (lu.statut === 'verifier') nbAVerifier++;
    marques[cle] = { etat: lu.statut, note: lu.note, ancien: actuel && !identique ? actuel : undefined };
  });
  return { voyageur: suivant, marques, nbRemplis, nbConfirmes, nbAVerifier, nbNonRepris };
};

const pluriel = (n: number, un: string, plusieurs: string) => `${n} ${n > 1 ? plusieurs : un}`;

// Résumé affiché au-dessus de la fiche ; bouton : libellé du bouton d'enregistrement de la fiche
// (« Valider les modifications » dans l'admin Legrand)
export const resumeLecture = (a: LectureAppliquee, bouton = 'Valider'): string => {
  const morceaux: string[] = [];
  if (a.nbRemplis) morceaux.push(pluriel(a.nbRemplis, 'champ pré-rempli', 'champs pré-remplis'));
  if (a.nbConfirmes) morceaux.push(pluriel(a.nbConfirmes, 'déjà juste', 'déjà justes'));
  let texte = morceaux.length ? `Passeport lu : ${morceaux.join(' et ')}` : 'Passeport lu : aucun champ à changer';
  if (a.nbAVerifier) texte += ` ; ${a.nbAVerifier} à vérifier (en orange)`;
  texte += '.';
  if (a.nbNonRepris) texte += ` ${pluriel(a.nbNonRepris, 'valeur non reprise', 'valeurs non reprises')} : champ déjà rempli.`;
  return `${texte} Rien n'est enregistré avant « ${bouton} ».`;
};

// Texte sous le champ
export const texteMarque = (m: MarqueChamp): string => {
  if (m.etat === 'non_repris') return `Passeport : « ${m.propose} » (non repris, champ déjà rempli)`;
  const debut = m.etat === 'sur' ? `✓ ${m.note}` : `À vérifier · ${m.note}`;
  return m.ancien ? `${debut} · avant : « ${m.ancien} »` : debut;
};

// Couleur MUI du champ marqué
export const couleurMarque = (m: MarqueChamp): 'success' | 'warning' | 'info' => (
  m.etat === 'sur' ? 'success' : (m.etat === 'verifier' ? 'warning' : 'info')
);
